import React from "react";
import styled from "styled-components";
import { AppDropdown } from "@/shared/components/Dropdown/AppDropdown";
import { Icon } from "@/shared/components/Icon/Icon";
import { PlusOutlined, MoreOutlined, MailOutlined } from "@ant-design/icons";
import type { Participant, ParticipantStatus } from "@/shared/types/campaign/types";
import { tableCardStyle, headerCellStyle, cellStyle, footerStyle, addLinkStyle } from "./styles";

interface Column {
	title: string;
	key: string;
}

interface Props {
	title: string;
	columns: Column[];
	data: Participant[];
	showRole?: boolean;
	showStatus?: boolean;
	onAdd?: () => void;
	onRemove?: (userId: string) => void;
	onResend?: (userId: string) => void;
	canResend?: (participant: Participant) => boolean;
	canManage?: boolean;
	canAdd?: boolean;
	canRemove?: boolean;
}

const STATUS_LABELS: Record<ParticipantStatus, string> = {
	active: "Active",
	invited: "Invited",
	expired: "Expired",
};

// Same tones as the engagement terms status badge (sent / accepted).
const StatusPill = styled.span<{ $status: ParticipantStatus }>`
	display: inline-flex;
	align-items: center;
	height: 24px;
	padding: 3px 10px;
	border-radius: 999px;
	background: ${(p) => (p.$status === "active" ? "#eaf9dc" : p.$status === "invited" ? "#e7f9ff" : "#f2f2f2")};
	color: ${(p) => (p.$status === "active" ? "#43851c" : p.$status === "invited" ? "#00a9d6" : "#8c8c8c")};
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
`;

export const ParticipantTable: React.FC<Props> = ({
	title,
	columns,
	data,
	showRole,
	showStatus,
	onAdd,
	onRemove,
	onResend,
	canResend,
	canManage = true,
	canAdd,
	canRemove,
}) => {
	const showAdd = Boolean(onAdd) && (canAdd ?? canManage);
	const showRemove = Boolean(onRemove) && (canRemove ?? canManage);
	const showActionCol = showAdd || showRemove;
	const colWidths = ["40%", "40%", "20%"];
	return (
		<div style={{ marginBottom: 32 }}>
			<div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>{title}</div>
			<div className="cv-table-card cv-participants" style={tableCardStyle}>
				<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
					<thead>
						<tr>
							{columns.map((c, i) => (
								<th key={c.key} style={{ ...headerCellStyle, width: colWidths[i] }} align="left">
									{c.title}
								</th>
							))}
							{columns.length < 3 && <th style={{ ...headerCellStyle, width: colWidths[2] }} />}
							{showActionCol && <th style={{ ...headerCellStyle, width: 48 }} />}
						</tr>
					</thead>
					<tbody>
						{data.map((p) => {
							const showResend =
								Boolean(onResend) && canManage && (canResend ? canResend(p) : p.status !== "active");
							return (
								<tr key={p.userId}>
									<td
										style={{
											...cellStyle,
											width: colWidths[0],
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{p.name || "—"}
									</td>
									<td
										style={{
											...cellStyle,
											width: colWidths[1],
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{p.email}
									</td>
									{showRole ? (
										<td style={{ ...cellStyle, width: colWidths[2] }}>{p.role}</td>
									) : showStatus && p.status ? (
										<td style={{ ...cellStyle, width: colWidths[2] }}>
											<StatusPill $status={p.status}>{STATUS_LABELS[p.status]}</StatusPill>
										</td>
									) : (
										<td style={{ ...cellStyle, width: colWidths[2] }} />
									)}
									{showActionCol && (
										<td
											className="cv-row-action"
											style={{ ...cellStyle, width: 48, textAlign: "center" }}
										>
											{showRemove && (
												<AppDropdown
													menu={{
														items: [
															...(showResend
																? [
																		{
																			key: "resend",
																			label: "Resend invite",
																			icon: (
																				<MailOutlined
																					style={{ fontSize: 16 }}
																				/>
																			),
																			onClick: () => onResend?.(p.userId),
																		},
																	]
																: []),
															{
																key: "remove",
																label: "Remove",
																danger: true,
																icon: (
																	<Icon
																		src="/icon/trash.svg"
																		size={18}
																		color="#D30024"
																	/>
																),
																onClick: () => onRemove?.(p.userId),
															},
														],
													}}
													trigger={["click"]}
													placement="bottomRight"
												>
													<MoreOutlined
														role="button"
														aria-label={`${p.name || p.email} actions`}
														style={{ cursor: "pointer", fontSize: 18 }}
													/>
												</AppDropdown>
											)}
										</td>
									)}
								</tr>
							);
						})}
					</tbody>
				</table>
				{showAdd && (
					<div style={footerStyle}>
						<button style={addLinkStyle} onClick={onAdd}>
							<PlusOutlined /> Add
						</button>
					</div>
				)}
			</div>
		</div>
	);
};
